import AbandonedCallsWidget from "@/components/widgets/call-overview/AbandonedCallsWidget";
import AverageHandleTimeWidget from "@/components/widgets/call-overview/AverageHandleTimeWidget";
import AverageSpeedOfAnswerWidget from "@/components/widgets/call-overview/AverageSpeedOfAnswerWidget";
import AverageWaitingTimeWidget from "@/components/widgets/call-overview/AverageWaitingTimeWidget";
import CallVolumeWidget from "@/components/widgets/call-overview/CallVolumeWidget";
import AverageCallsPerAgentWidget from "@/components/widgets/agent-performance/AverageCallsPerAgentWidget";
import type { CallsDetailOverviewWidgetData } from "@/components/widgets/call-overview/CallsDetailOverviewWidget";
import CallsDetailOverviewWidget from "@/components/widgets/call-overview/CallsDetailOverviewWidget";
import CallsHandledByQueueWidget from "@/components/widgets/call-overview/CallsHandledByQueueWidget";
import InboundCallStatusWidget from "@/components/widgets/call-overview/InboundCallStatusWidget";
import InboundCallsWidget from "@/components/widgets/call-overview/InboundCallsWidget";
import TotalTransferredCallsWidget from "@/components/widgets/call-overview/TotalTransferredCallsWidget";
import TotalMissedCallsWidget from "@/components/widgets/call-overview/TotalMissedCallsWidget";
import { CallQueueType } from "@/constants/queue";
import { SectionType } from "@/constants/section";
import { WIDGET_IDS, WIDGET_NAMES } from "@/constants/widget";
import { useAppData, UserRole } from "@/contexts/AppDataContext";
import { useDashboardData } from "@/contexts/DashboardContext";
import { useWidgetsData } from "@/hooks/api/useWidgetsData";
import { useFilterCache } from "@/hooks/useFilterCache";
import { DateRangeChange, FilterChangeEvent } from "@/types";
import { calculateDateRange, DATE_RANGE_KEYS, getRecalculatedDatesIfNeeded } from '@/utils/dateRangeUtils';
import React, { useMemo } from "react";
import FilterBar from "../../common/FilterBar";
import { CallDirectionTypes } from "@/enums/call-direction";
import { CallsOverviewTableColumns } from "@/enums/call-overvew-column";

const widgetIds = [
  WIDGET_IDS.TOTAL_INBOUND_CALLS,        // 1
  WIDGET_IDS.TOTAL_INBOUND_CALLS_MISSED, // 4
  WIDGET_IDS.TOTAL_ABANDONED_CALLS,      // 8
  WIDGET_IDS.TOTAL_INBOUND_TRANSFERRED_CALLS,    // 40
  WIDGET_IDS.AVERAGE_WAITING_TIME,       // 13
  WIDGET_IDS.AVERAGE_HANDLE_TIME,        // 14
  WIDGET_IDS.AVERAGE_SPEED_OF_ANSWER,    // 15
  WIDGET_IDS.CALL_VOLUME,                // 19
  WIDGET_IDS.INBOUND_STATUS_DISTRIBUTION, // 20
  WIDGET_IDS.CALLS_HANDLED_BY_QUEUE,     // 22
  WIDGET_IDS.AVERAGE_CALLS_PER_AGENT_CQ,    // 43
];
interface CallOverviewWidgetsProps {
  isGeneralMode: boolean;
  filterClass?: string;
}


const CallOverviewSection: React.FC<CallOverviewWidgetsProps> = ({ isGeneralMode, filterClass }) => {
  const defaultDateRangeKey = DATE_RANGE_KEYS.YESTERDAY;
  const { current } = useDashboardData();
  const { filters, saveFilters } = useFilterCache("call-overview", current?.id);
  const [dateRangeKey, setDateRangeKey] = React.useState<string>(filters?.dateRangeKey ?? defaultDateRangeKey);
  const [callsDetailFilters, setCallsDetailFilters] = React.useState<{ [key: string]: string[] }>({});
  const callsDetailWidgetRef = React.useRef<HTMLDivElement>(null);
  const [dateRange, setDateRange] = React.useState<DateRangeChange>({
    key: filters?.dateRangeKey ?? defaultDateRangeKey,
    startDate: filters?.startDate ?? null,
    endDate: filters?.endDate ?? null,
    previousStartDate: filters?.previousStartDate ?? null,
    previousEndDate: filters?.previousEndDate ?? null,
  });
  const { directAgentGroupIds, queueMicrosoftIds, userRole } = useAppData();

  const getArrayOrAll = (array: string[]) => {
    if (userRole === UserRole.ADMIN && (!array || array.length === 0)) {
      return null;
    }
    return array?.length > 0 ? array : queueMicrosoftIds;
  };
  // State for date range filter
  const [dateParams, setDateParams] = React.useState(() => {
    if (filters) {
      const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
        filters.dateRangeKey,
        filters
      );
      
      setDateRange({
        key: filters?.dateRangeKey,
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
      });
      return {
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
        widgetIds,
        queueMicrosoftIds: filters.selectedQueueType === CallQueueType.CALL_QUEUE ? getArrayOrAll(filters.queues) : null,
        agentMicrosoftIds: filters.agents?.length ? filters.agents : null,
        groupIds: filters.selectedQueueType === CallQueueType.DIRECT_AGENT_GROUP
          ? directAgentGroupIds.filter(id => filters.queues.includes(id.toString()) || filters.queues.length === 0)
          : null,
        timeStamp: new Date().toISOString()
      };
    }

    const dateRange = calculateDateRange(defaultDateRangeKey);
    return {
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
      previousStartDate: dateRange.previousFrom.toISOString(),
      previousEndDate: dateRange.previousTo.toISOString(),
      widgetIds,
      queueMicrosoftIds: userRole === UserRole.ADMIN ? null : queueMicrosoftIds,
      agentMicrosoftIds: null,
      groupIds: null,
      timeStamp: new Date().toISOString()
    };
  });

  const handleFilterChange = async (event: FilterChangeEvent) => {
    const queues = event.queues || [];
    setDateRangeKey(event.dateRange.key);
    
    // Recalculate dates for dynamic date ranges to ensure freshness
    const recalculatedDates = getRecalculatedDatesIfNeeded(event.dateRange.key, {
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
    });
    
    setDateRange({
      key: event.dateRange.key,
      startDate: recalculatedDates.startDate,
      endDate: recalculatedDates.endDate,
      previousStartDate: recalculatedDates.previousStartDate,
      previousEndDate: recalculatedDates.previousEndDate,
    });
    setDateParams(prev => ({
      ...prev,
      startDate: recalculatedDates.startDate,
      endDate: recalculatedDates.endDate,
      previousStartDate: recalculatedDates.previousStartDate,
      previousEndDate: recalculatedDates.previousEndDate,
      queueMicrosoftIds: event.selectedQueueType === CallQueueType.CALL_QUEUE ? getArrayOrAll(event.queues) : null,
      agentMicrosoftIds: null, // disable agent filter for call overview
      groupIds: event.selectedQueueType === CallQueueType.DIRECT_AGENT_GROUP
        ? directAgentGroupIds.filter(id => event.queues.includes(id.toString()) || queues.length === 0)
        : null,
      timeStamp: new Date().toISOString()
    }));

    // persist
    saveFilters({
      dateRangeKey: event.dateRange.key,
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
      queues: event.queues ?? [],
      agents: event.agents ?? [],
      selectedQueueType: event.selectedQueueType,
    });

    setCallsDetailFilters({}); // reset calls detail filters
  };

  // Refresh data when tab becomes active
  React.useEffect(() => {
    if (isGeneralMode) {
      setDateParams(prev => ({
        ...prev,
        timeStamp: new Date().toISOString()
      }));
    }
  }, [isGeneralMode]);

  // Call widgets API when dateParams changes
  const { data: widgetsData, loading: widgetsLoading, error: widgetsError } = useWidgetsData(dateParams);

  const handleWidgetClick = (filters: { [key: string]: string[] }) => {
    // Set filter from the widget
    setCallsDetailFilters(filters);
    
    // Scroll to CallsDetailOverviewWidget
    setTimeout(() => {
      if (callsDetailWidgetRef.current) {
        callsDetailWidgetRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  // Derived, typed datasets for widgets
  const getString = (value: unknown, fallback = ""): string => {
    return typeof value === "string" ? value : fallback;
  };
  const getNumber = (value: unknown, fallback = 0): number => {
    if (typeof value === "number") return value;
    if (typeof value === "string" && value.trim() !== "" && !isNaN(Number(value))) return Number(value);
    return fallback;
  };
  type DirectionType = CallsDetailOverviewWidgetData["direction"];
  const toDirection = React.useCallback((value: unknown): DirectionType => {
    const v = typeof value === "string" ? value.toLowerCase() : "";
    if (v === "inbound direct" || v === "inbound") return "Inbound";
    if (v === "outbound") return "Outbound";
    if (v === "internal") return "Internal";
    return "Inbound";
  }, []);

  const inboundStatusData = useMemo(() => ({
    answeredCall: getNumber(widgetsData?.callOverviewData?.inboundStatusDistribution?.answeredCall),
    missedCall: getNumber(widgetsData?.callOverviewData?.inboundStatusDistribution?.missedCall),
    totalAbandonedCalls: getNumber(widgetsData?.callOverviewData?.inboundStatusDistribution?.totalAbandonedCalls)
  }), [widgetsData?.callOverviewData?.inboundStatusDistribution]);


  const callsHandledByQueueData = useMemo(() => (
    (widgetsData?.callOverviewData?.callsHandledByQueue ?? []).map((raw) => {
      const item = raw as Record<string, unknown>;
      return {
        queueName: getString(item.queueName),
        totalHandledCalls: getNumber(item.totalHandledCalls)
      };
    })
  ), [widgetsData?.callOverviewData?.callsHandledByQueue]);

  const callVolumeData = useMemo(() => (
    (widgetsData?.callOverviewData?.callVolume ?? []).map((raw) => {
      const item = raw as Record<string, unknown>;
      const timeStr = getString(item.time, new Date().toISOString());
      return {
        time: new Date(timeStr),
        inboundCall: getNumber(item.inboundCall),
        outboundCall: getNumber(item.outboundCall)
      };
    })
  ), [widgetsData?.callOverviewData?.callVolume]);


  const renderWidget = (
    widgetId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    WidgetComponent: React.ComponentType<any>,
    data?: Record<string, unknown>
  ) => {
    return (
      <WidgetComponent
        key={widgetId}
        data={data as unknown}
        isGeneralMode={true}
        loading={widgetsLoading}
        error={widgetsError}
      />
    );
  };

  const renderOverviewSection = () => {
    return <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4 mb-4">
        <InboundCallsWidget
          key={WIDGET_NAMES.TOTAL_INBOUND_CALLS}
          data={{
            value: getNumber(widgetsData?.callOverviewData?.totalInboundCalls?.totalInboundCalls) || null,
            comparisonPercentage: widgetsData?.callOverviewData?.totalInboundCalls?.comparisonPercentage != null 
              ? getNumber(widgetsData?.callOverviewData?.totalInboundCalls?.comparisonPercentage) 
              : null
          }}
          loading={widgetsLoading}
          error={widgetsError}
          onClick={handleWidgetClick}
        />

        <TotalMissedCallsWidget
          key={WIDGET_NAMES.TOTAL_INBOUND_CALLS_MISSED}
          data={{
            value: getNumber(widgetsData?.callOverviewData?.totalInboundCallsMissed?.totalInboundCallsMissed) || null,
            comparisonPercentage: widgetsData?.callOverviewData?.totalInboundCallsMissed?.comparisonPercentage != null 
              ? getNumber(widgetsData?.callOverviewData?.totalInboundCallsMissed?.comparisonPercentage) 
              : null
          }}
          loading={widgetsLoading}
          error={widgetsError}
          onClick={handleWidgetClick}
        />

        <TotalTransferredCallsWidget
          key={WIDGET_NAMES.TOTAL_INBOUND_TRANSFERRED_CALLS}
          data={{
            value: getNumber(widgetsData?.callOverviewData?.['totalInboundTransferredCalls']?.['totalTransferredCalls']),
            comparisonPercentage: widgetsData?.callOverviewData?.['totalInboundTransferredCalls']?.['comparisonPercentage'] != null 
              ? getNumber(widgetsData?.callOverviewData?.['totalInboundTransferredCalls']?.['comparisonPercentage']) 
              : null,
            callMicrosoftIds: (widgetsData?.callOverviewData?.['totalInboundTransferredCalls'] as { callMicrosoftIds?: string[] })?.callMicrosoftIds
          }}
          loading={widgetsLoading}
          error={widgetsError}
          onClick={handleWidgetClick}
        />

        <AbandonedCallsWidget
          key={WIDGET_NAMES.TOTAL_ABANDONED_CALLS}
          data={{
            value: getNumber(widgetsData?.callOverviewData?.totalAbandonedCalls?.totalAbandonedCalls) || null,
            comparisonPercentage: widgetsData?.callOverviewData?.totalAbandonedCalls?.comparisonPercentage != null 
              ? getNumber(widgetsData?.callOverviewData?.totalAbandonedCalls?.comparisonPercentage) 
              : null
          }}
          loading={widgetsLoading}
          error={widgetsError}
          onClick={handleWidgetClick}
        />

        {renderWidget(WIDGET_NAMES.AVERAGE_WAITING_TIME, AverageWaitingTimeWidget, {
          value: widgetsData?.callOverviewData?.averageWaitingTime?.averageWaitingTime,
          comparisonPercentage: widgetsData?.callOverviewData?.averageWaitingTime?.comparisonPercentage != null 
            ? widgetsData.callOverviewData.averageWaitingTime.comparisonPercentage 
            : null
        })}

        {renderWidget(WIDGET_NAMES.AVERAGE_HANDLE_TIME, AverageHandleTimeWidget, {
          value: widgetsData?.callOverviewData?.averageHandleTime?.averageHandleTime,
          comparisonPercentage: widgetsData?.callOverviewData?.averageHandleTime?.comparisonPercentage != null 
            ? widgetsData.callOverviewData.averageHandleTime.comparisonPercentage 
            : null
        })}

        {renderWidget(WIDGET_NAMES.AVERAGE_SPEED_OF_ANSWER, AverageSpeedOfAnswerWidget, {
          value: widgetsData?.callOverviewData?.averageSpeedOfAnswer?.averageSpeedOfAnswer,
          comparisonPercentage: widgetsData?.callOverviewData?.averageSpeedOfAnswer?.comparisonPercentage != null 
            ? widgetsData.callOverviewData.averageSpeedOfAnswer.comparisonPercentage 
            : null
        })}

        {renderWidget(WIDGET_NAMES.AVERAGE_CALLS_PER_AGENT_CQ, AverageCallsPerAgentWidget, {
          value: getNumber(widgetsData?.averageCallsPerAgentCQ),
        })}
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4">
          <InboundCallStatusWidget
            loading={widgetsLoading}
            error={widgetsError}
            data={inboundStatusData}
            onClick={handleWidgetClick} />

          <CallsHandledByQueueWidget
            loading={widgetsLoading}
            error={widgetsError}
            data={callsHandledByQueueData}
          />
        </div>
        <div className="grid grid-cols-1 gap-4">
          <CallVolumeWidget
            loading={widgetsLoading}
            error={widgetsError}
            data={callVolumeData}
            dateRangeType={dateRangeKey}
            dateRange={{ from: new Date(dateParams.startDate), to: new Date(dateParams.endDate) }}
          />
        </div>
      </div>
    </>
  }

  return (
    <div className="w-full">
      <div className={filterClass}>
        <FilterBar
          onFilterChange={handleFilterChange}
          initialDateRangeKey={dateRangeKey}
          initialSelectedQueues={filters?.queues}
          initialSelectedAgents={filters?.agents}
          initialSelectedQueueType={filters?.selectedQueueType}
          initialDateRange={dateRange}
          sectionType={SectionType.CALL_OVERVIEW}
        />
      </div>

      {renderOverviewSection()}

      <div className="grid grid-cols-1 gap-4 mt-4" ref={callsDetailWidgetRef}>
        <CallsDetailOverviewWidget
          key={JSON.stringify(callsDetailFilters)}
          dateParams={dateParams}
          callDirectionTypes={[CallDirectionTypes.INBOUND]}
          initialFilters={callsDetailFilters}
          hiddenColumns={[CallsOverviewTableColumns.RECIPIENT]}
        />
      </div>
    </div>
  );
};

export default CallOverviewSection;
